'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useRef, useState, useSyncExternalStore, type CSSProperties, type PointerEvent } from 'react';
import { useTheme } from 'next-themes';
import type { ClothingItem, Outfit } from '@/types/closet';
import { attachScrubber } from './scrub';
import { GridScrollFade } from '../GridScrollFade';
import { bindTimelineScene, wheelPixels } from './timeline-motion';
import { PreviewLoop } from './PreviewLoop';
import { GarmentOverlay } from './GarmentOverlay';
import { MediaReview } from './MediaReview';
import { OutfitDrafts } from './OutfitDrafts';
import { OutfitDetails } from './OutfitDetails';
import { warmLook } from './warm';
import { getLookbookPath, navigateLookbook, orderedLooks, outfitPath, selectedLook, subscribeNavigation } from './navigation';
import { hasTimelineMedia } from '@/lib/closet/timeline-media';
import './lookbook.css';
import './outfit-timeline.css';

// Touch devices and Save-Data connections fetch only the rotation's metadata
// until the visitor interacts; the scrubber's first play()/seek pulls the rest.
// Someone opening a shared look on a phone shouldn't pay for a full rotation
// video before touching it.
function shouldDeferRotation() {
  const connection = (navigator as Navigator & {
    connection?: {
      saveData?: boolean;
    };
  }).connection;
  return connection?.saveData === true || window.matchMedia('(pointer: coarse)').matches;
}
const subscribeHydration = () => () => {};
const clientReady = () => true;
const serverReady = () => false;
function revealSelectedOutfit(node: HTMLButtonElement | null) {
  const picker = node?.parentElement;
  if (!node || !picker) return;
  const buttonBounds = node.getBoundingClientRect();
  const pickerBounds = picker.getBoundingClientRect();
  picker.scrollTo({
    left: picker.scrollLeft + buttonBounds.left - pickerBounds.left - (picker.clientWidth - buttonBounds.width) / 2,
    behavior: 'smooth'
  });
}
export function OutfitTimeline(t0) {
  const $ = _c(51);
  const {
    outfits: sourceOutfits,
    items,
    initialId,
    onCreate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [updates, setUpdates] = useState(t1);
  const allOutfits = orderedLooks(Object.values({
    ...Object.fromEntries(sourceOutfits.map(_temp)),
    ...updates
  }));
  const outfits = allOutfits.filter(hasTimelineMedia);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function updateOutfit(updated) {
      setUpdates(current => ({
        ...current,
        [updated.id]: updated
      }));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const updateOutfit = t2;
  let t3;
  if ($[2] !== initialId) {
    t3 = () => initialId ? outfitPath(initialId) : "/closet";
    $[2] = initialId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const path = useSyncExternalStore(subscribeNavigation, getLookbookPath, t3);
  const selected = selectedLook(path, outfits);
  const active = selected;
  const [overviewIndex, setOverviewIndex] = useState(() => Math.floor(outfits.length / 2));
  const anchor = active < 0 ? overviewIndex : active;
  const outfit_0 = outfits[active];
  const [detailsId, setDetailsId] = useState(null);
  const detailsOpen = !!outfit_0 && detailsId === outfit_0.id;
  const hydrated = useSyncExternalStore(subscribeHydration, clientReady, serverReady);
  const {
    resolvedTheme
  } = useTheme();
  const dark = hydrated && resolvedTheme === "dark";
  const [selectedItem, setSelectedItem] = useState(null);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      degree: 0,
      ready: false,
      failed: false
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [rotation, setRotation] = useState(t4);
  const controller = useRef(null);
  let t5;
  if ($[5] !== setRotation) {
    t5 = video => {
      if (!video) {
        return;
      }
      if (shouldDeferRotation()) {
        video.preload = "metadata";
      }
      setRotation({
        degree: 0,
        ready: video.readyState >= 1,
        failed: false
      });
      const attached = attachScrubber(video, degree => setRotation(current_0 => current_0.degree === degree ? current_0 : {
        ...current_0,
        degree
      }));
      controller.current = attached;
      return () => {
        attached.dispose();
        if (controller.current === attached) {
          controller.current = null;
        }
      };
    };
    $[5] = setRotation;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const attachVideo = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = node => {
      if (!node) {
        return;
      }
      const onWheel = event => {
        if (event.ctrlKey) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const current_1 = controller.current;
        current_1?.activate();
        current_1?.nudge(wheelPixels(event) * 0.6);
      };
      node.addEventListener("wheel", onWheel, {
        passive: false
      });
      return () => node.removeEventListener("wheel", onWheel);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const rotateOnWheel = t6;
  const select = function select(index) {
    if (!outfits[index]) {
      return;
    }
    if (index === active) {
      setDetailsId(outfits[index].id);
      return;
    }
    setDetailsId(null);
    navigateLookbook(outfits[index].id);
  };
  const step = function step(direction) {
    if (active < 0) {
      setOverviewIndex(index_0 => Math.max(0, Math.min(outfits.length - 1, index_0 + direction)));
    } else {
      select(active + direction);
    }
  };
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = function seek(degree_0) {
      controller.current?.activate();
      controller.current?.seek(degree_0);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const seek = t7;
  let t8;
  if ($[9] !== seek) {
    t8 = function hover(event_0) {
      if (event_0.pointerType !== "mouse" && !event_0.currentTarget.hasPointerCapture(event_0.pointerId)) {
        return;
      }
      const bounds = event_0.currentTarget.getBoundingClientRect();
      seek(Math.round(Math.max(0, Math.min(1, (event_0.clientX - bounds.left) / bounds.width)) * 360));
    };
    $[9] = seek;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const hover = t8;
  let t9;
  if ($[11] !== active) {
    t9 = event_1 => {
      if (event_1.key === "Escape" && active >= 0) {
        navigateLookbook();
      }
    };
    $[11] = active;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== onCreate) {
    t10 = onCreate && <button type="button" aria-label="Create outfit" onClick={onCreate}>+</button>;
    $[13] = onCreate;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = "outfit-picker";
  const t12 = "Choose outfit";
  const t13 = active < 0 ? "ALL" : String(active + 1).padStart(2, "0");
  const t14 = String(outfits.length);
  let t15;
  if ($[15] !== t14) {
    t15 = t14.padStart(2, "0");
    $[15] = t14;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] !== t15) {
    t16 = <span>/ {t15}</span>;
    $[17] = t15;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t13 || $[20] !== t16) {
    t17 = <span>{t13}{" "}{t16}</span>;
    $[19] = t13;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const t18 = outfits.map((look, index_1) => <button type="button" key={look.id} aria-label={look.name} aria-pressed={active === index_1} ref={active === index_1 ? revealSelectedOutfit : undefined} onClick={() => select(index_1)} onPointerEnter={() => warmLook(look, dark)} onFocus={() => warmLook(look, dark)}><Image unoptimized={true} src={look.lookbookMedia.imageUrl} alt="" width={28} height={40} draggable={false} /></button>);
  let t19;
  if ($[22] !== t17 || $[23] !== t18) {
    t19 = <div className={t11} aria-label={t12}>{t17}{t18}</div>;
    $[22] = t17;
    $[23] = t18;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== active || $[26] !== anchor || $[27] !== attachVideo || $[28] !== dark || $[29] !== hover || $[30] !== hydrated || $[31] !== items || $[32] !== outfit_0 || $[33] !== outfits || $[34] !== rotateOnWheel || $[35] !== rotation || $[36] !== seek || $[37] !== select || $[38] !== setRotation || $[39] !== setSelectedItem || $[40] !== step || $[41] !== updateOutfit) {
    t20 = outfits.length > 0 ? <><TimelineModels outfits={outfits} active={active} step={step} rotateOnWheel={rotateOnWheel} seek={seek} degree={rotation.degree} dark={dark} hydrated={hydrated} failed={rotation.failed} attachVideo={attachVideo} hover={hover} select={select} onReady={() => setRotation(_temp4)} onError={() => setRotation(_temp5)} />{outfit_0 && <aside className="outfit-caption"><h1 key={outfit_0.id}>{outfit_0.name}</h1>{rotation.failed && <p role="status" className="outfit-media-error">Rotation unavailable</p>}<MediaReview key={`media-${outfit_0.id}`} outfit={outfit_0} onUpdate={updateOutfit} /></aside>}<footer className="outfit-footer"><div className="outfit-items">{outfit_0?.items.flatMap(id => {
            const item_0 = items.find(item => item.id === id);
            return item_0 ? [<button type="button" key={item_0.id} onClick={() => setSelectedItem(item_0)}><Image unoptimized={true} src={item_0.imageUrl} alt="" width={30} height={34} draggable={false} /><span>{item_0.name}</span></button>] : [];
          })}</div><nav aria-label="Navigate outfits"><button type="button" aria-label="Previous outfit" disabled={anchor === 0} onClick={() => step(-1)} onPointerEnter={() => warmLook(outfits[anchor - 1], dark)} onFocus={() => warmLook(outfits[anchor - 1], dark)}>←</button><button type="button" aria-label="Next outfit" disabled={anchor === outfits.length - 1} onClick={() => step(1)} onPointerEnter={() => warmLook(outfits[anchor + 1], dark)} onFocus={() => warmLook(outfits[anchor + 1], dark)}>→</button></nav></footer></> : <p className="outfit-empty">No outfits yet</p>;
    $[25] = active;
    $[26] = anchor;
    $[27] = attachVideo;
    $[28] = dark;
    $[29] = hover;
    $[30] = hydrated;
    $[31] = items;
    $[32] = outfit_0;
    $[33] = outfits;
    $[34] = rotateOnWheel;
    $[35] = rotation;
    $[36] = seek;
    $[37] = select;
    $[38] = setRotation;
    $[39] = setSelectedItem;
    $[40] = step;
    $[41] = updateOutfit;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== selectedItem || $[44] !== setSelectedItem) {
    t21 = selectedItem && <GarmentOverlay item={selectedItem} onClose={() => setSelectedItem(null)} />;
    $[43] = selectedItem;
    $[44] = setSelectedItem;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== detailsOpen || $[47] !== items || $[48] !== outfit_0 || $[49] !== setDetailsId) {
    t22 = detailsOpen && <OutfitDetails outfit={outfit_0} items={outfit_0.items.flatMap(id_0 => {
      const item_2 = items.find(item_1 => item_1.id === id_0);
      return item_2 ? [item_2] : [];
    })} onClose={() => setDetailsId(null)} />;
    $[46] = detailsOpen;
    $[47] = items;
    $[48] = outfit_0;
    $[49] = setDetailsId;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  return <main className="lookbook outfit-timeline" data-hydrated={hydrated} data-overview={active < 0} data-details={detailsOpen} style={{
    "--outfit-count": outfits.length,
    "--outfit-anchor": anchor
  } as CSSProperties} onKeyDown={t9} id="main-content" onDragStart={_temp2} onContextMenu={_temp3}><header className="outfit-topbar"><div className="outfit-actions">{t10}<OutfitDrafts outfits={allOutfits} onUpdate={updateOutfit} /></div>{t19}</header>{t20}{t21}{t22}</main>;
}
function _temp5(current_3) {
  return {
    ...current_3,
    ready: false,
    failed: true
  };
}
function _temp4(current_2) {
  return {
    ...current_2,
    ready: true
  };
}
function _temp3(event_3) {
  return event_3.preventDefault();
}
function _temp2(event_2) {
  return event_2.preventDefault();
}
function _temp(outfit) {
  return [outfit.id, outfit];
}
function TimelineModels(t0) {
  const $ = _c(16);
  const {
    outfits,
    active,
    step,
    rotateOnWheel,
    seek,
    degree,
    dark,
    hydrated,
    failed,
    attachVideo,
    hover,
    select,
    onReady,
    onError
  } = t0;
  const swipe = useRef(null);
  const rotationStart = useRef(null);
  const dragged = useRef(false);
  const t1 = "outfit-track-window";
  const t2 = bindTimelineScene;
  let t3;
  if ($[0] !== active || $[1] !== dark || $[2] !== outfits) {
    t3 = event => {
      dragged.current = false;
      if (event.pointerType !== "mouse") {
        swipe.current = {
          x: event.clientX,
          y: event.clientY
        };
        warmLook(outfits[active - 1], dark);
        warmLook(outfits[active + 1], dark);
      }
    };
    $[0] = active;
    $[1] = dark;
    $[2] = outfits;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== step) {
    t4 = event_0 => {
      const start = swipe.current;
      swipe.current = null;
      if (!start) {
        return;
      }
      const dx = event_0.clientX - start.x;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(event_0.clientY - start.y)) {
        dragged.current = true;
        step(dx < 0 ? 1 : -1);
      }
    };
    $[4] = step;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      swipe.current = null;
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = "outfit-model-track";
  const t7 = outfits.map((look, index) => {
    const focused = index === active;
    const lookMedia = look.lookbookMedia;
    const src = dark ? lookMedia.rotationUrlDark : lookMedia.rotationUrl;
    return <div key={look.id} className="outfit-model-cell" data-focused={focused} data-distance={Math.min(3, Math.abs(index - active))} style={{
      "--model-index": index
    } as CSSProperties}><div className="outfit-model-enter"><button type="button" className="outfit-model" tabIndex={active >= 0 && Math.abs(index - active) > 2 ? -1 : 0} aria-label={focused ? `View ${look.name} details` : `Show ${look.name}`} ref={focused ? rotateOnWheel : undefined} onPointerEnter={focused ? undefined : () => warmLook(look, dark)} onClick={() => {
          if (!dragged.current) {
            select(index);
          }
          dragged.current = false;
        }} onKeyDown={focused ? event_1 => {
          const next = event_1.key === "ArrowRight" ? degree + 10 : event_1.key === "ArrowLeft" ? degree - 10 : event_1.key === "Home" ? 0 : event_1.key === "End" ? 360 : null;
          if (next === null) {
            return;
          }
          event_1.preventDefault();
          seek(Math.max(0, Math.min(360, next)));
        } : undefined} onPointerDown={focused ? event_2 => {
          if (event_2.pointerType === "mouse") {
            return;
          }
          dragged.current = false;
          rotationStart.current = event_2.clientX;
          event_2.stopPropagation();
          event_2.currentTarget.setPointerCapture(event_2.pointerId);
          hover(event_2);
        } : undefined} onPointerUp={focused ? event_3 => {
          dragged.current = rotationStart.current !== null && Math.abs(event_3.clientX - rotationStart.current) > 5;
          rotationStart.current = null;
          if (event_3.currentTarget.hasPointerCapture(event_3.pointerId)) {
            event_3.currentTarget.releasePointerCapture(event_3.pointerId);
            event_3.stopPropagation();
          }
        } : undefined} onPointerMove={focused ? hover : undefined}><Image unoptimized={true} src={lookMedia.imageUrl} alt={look.name} width={1024} height={1536} priority={focused} sizes="(max-width: 900px) 90vw, 55vw" draggable={false} />{hydrated && !focused && <PreviewLoop src={dark ? lookMedia.previewUrlDark : lookMedia.previewUrl} poster={lookMedia.imageUrl} />}{hydrated && focused && <video key={src} data-failed={failed} ref={attachVideo} src={src} poster={lookMedia.imageUrl} playsInline={true} muted={true} preload="auto" aria-label={`${look.name}, full rotation`} onLoadedMetadata={onReady} onError={onError} />}</button></div></div>;
  });
  let t8;
  if ($[7] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <GridScrollFade axis="horizontal" />;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== active || $[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== t8) {
    t10 = <div className={t1} data-active={active} ref={t2} onPointerDown={t3} onPointerUp={t4} onPointerCancel={t5}>{t8}{t9}</div>;
    $[10] = active;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t8;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}